/**
 * Local client-side mirror of the game world state.
 * Updated by server messages (WorldSnapshot / DeltaState) and rendered each frame.
 */

import { ServerMessage, BuildingState, PuppetState, PlayerState, ResourceState, PollutionState, TechStage, BuildingKind, WorldSnapshot, EntityDelta, BuildingUnlockInfo } from './client_message_protocol';

export interface ResearchState {
  active: boolean;
  progress: number;
  duration: number;
  targetStage: string;
}

export interface DamageEvent {
  targetId: number;
  amount: number;
  attackerId: number;
}

/** Hardcoded unlock map: which stage unlocks each building kind */
export const BUILDING_UNLOCK_STAGE: Record<BuildingKind, TechStage> = {
  // Foundation
  [BuildingKind.SpiritRoot]: TechStage.Foundation,
  [BuildingKind.GatheringArray]: TechStage.Foundation,
  [BuildingKind.TransporterStation]: TechStage.Foundation,
  [BuildingKind.HerbFarm]: TechStage.Foundation,
  // GoldenCore
  [BuildingKind.RefiningArray]: TechStage.GoldenCore,
  [BuildingKind.AlchemyFurnace]: TechStage.GoldenCore,
  [BuildingKind.Forge]: TechStage.GoldenCore,
  [BuildingKind.SwordTower]: TechStage.GoldenCore,
  [BuildingKind.SlowTrap]: TechStage.GoldenCore,
  // NascentSoul
  [BuildingKind.PuppetWorkshop]: TechStage.NascentSoul,
  [BuildingKind.DefenseTower]: TechStage.NascentSoul,
  [BuildingKind.PuppetBarracks]: TechStage.NascentSoul,
};

const TECH_ORDER: TechStage[] = [TechStage.Foundation, TechStage.GoldenCore, TechStage.NascentSoul];

export class GameClientWorldState {
  players: PlayerState[] = [];
  buildings: Map<number, BuildingState> = new Map();
  puppets: Map<number, PuppetState> = new Map();
  resources: ResourceState[] = [];
  pollution: PollutionState[] = [];
  unlockInfo: BuildingUnlockInfo[] = [];
  techStage: TechStage = TechStage.Foundation;
  spiritQi: number = 0;
  spiritStone: number = 0;
  herb: number = 0;
  pill: number = 0;
  artifact: number = 0;
  puppetCores: number = 0;
  localPlayerId: number | null = null;
  researchState: ResearchState | null = null;

  // Position proxies for floating text origin
  spiritQiPos: { x: number; y: number } | null = null;
  spiritStonePos: { x: number; y: number } | null = null;
  herbPos: { x: number; y: number } | null = null;
  pillPos: { x: number; y: number } | null = null;
  artifactPos: { x: number; y: number } | null = null;

  private buildingTimestamps: Map<number, number> = new Map();
  private puppetTimestamps: Map<number, number> = new Map();
  private damageQueue: DamageEvent[] = [];

  applyWorldSnapshot(snapshot: WorldSnapshot): void {
    this.players = snapshot.players;
    this.unlockInfo = snapshot.unlock_info || [];

    const now = Date.now();
    const MERGE_WINDOW_MS = 2000;

    // --- Buildings: smart merge ---
    const snapshotBuildingIds = new Set(snapshot.buildings.map(b => b.id));
    // Merge snapshot buildings into local map
    for (const b of snapshot.buildings) {
      this.buildings.set(b.id, b);
    }
    // Remove local buildings not in snapshot, but only if they're older than the merge window
    for (const [id, _b] of this.buildings) {
      if (!snapshotBuildingIds.has(id)) {
        const ts = this.buildingTimestamps.get(id) ?? 0;
        if (now - ts > MERGE_WINDOW_MS) {
          this.buildings.delete(id);
          this.buildingTimestamps.delete(id);
        }
      }
    }

    // --- Puppets: smart merge ---
    const snapshotPuppetIds = new Set(snapshot.puppets.map(p => p.id));
    // Merge snapshot puppets into local map
    for (const p of snapshot.puppets) {
      this.puppets.set(p.id, p);
    }
    // Remove local puppets not in snapshot, but only if they're older than the merge window
    for (const [id, _p] of this.puppets) {
      if (!snapshotPuppetIds.has(id)) {
        const ts = this.puppetTimestamps.get(id) ?? 0;
        if (now - ts > MERGE_WINDOW_MS) {
          this.puppets.delete(id);
          this.puppetTimestamps.delete(id);
        }
      }
    }

    this.resources = snapshot.resources;
    this.pollution = snapshot.pollution;
    // Extract resource pools and tech_stage from the local player
    if (snapshot.players.length > 0) {
      const me = this.localPlayerId !== null
        ? snapshot.players.find(p => p.id === this.localPlayerId)
        : snapshot.players[0];
      if (me) {
        this.spiritQi = me.spirit_qi;
        this.spiritStone = me.spirit_stone;
        this.herb = me.herb;
        this.pill = me.pill;
        this.artifact = me.artifact;
        this.puppetCores = me.puppet_cores;
        this.techStage = me.tech_stage ?? TechStage.Foundation;
      }
    }
  }

  applyDelta(deltas: EntityDelta[]): void {
    for (const d of deltas) {
      if (d.component === '_removed') {
        this.buildings.delete(d.entity_id);
        this.puppets.delete(d.entity_id);
        this.buildingTimestamps.delete(d.entity_id);
        this.puppetTimestamps.delete(d.entity_id);
      } else if (d.component === 'building') {
        const b = d.data as BuildingState;
        this.buildings.set(b.id, b);
        this.buildingTimestamps.set(b.id, Date.now());
      } else if (d.component === 'player') {
        const p = d.data as PlayerState;
        const idx = this.players.findIndex(pl => pl.id === p.id);
        if (idx >= 0) {
          this.players[idx] = p;
        }
        // Update local resource tracking and tech stage
        if (p.id === this.localPlayerId) {
          this.spiritQi = p.spirit_qi;
          this.spiritStone = p.spirit_stone;
          this.herb = p.herb;
          this.pill = p.pill;
          this.artifact = p.artifact;
          this.puppetCores = p.puppet_cores;
          this.techStage = p.tech_stage ?? TechStage.Foundation;
        }
      } else if (d.component === 'puppet') {
        const p = d.data as PuppetState;
        this.puppets.set(p.id, p);
        this.puppetTimestamps.set(p.id, Date.now());
      }
    }
  }

  /** Whether the given building kind is unlocked at the current tech stage */
  isUnlocked(buildingKind: BuildingKind): boolean {
    const requiredStage = BUILDING_UNLOCK_STAGE[buildingKind] ?? TechStage.NascentSoul;
    const currentIdx = TECH_ORDER.indexOf(this.techStage);
    const requiredIdx = TECH_ORDER.indexOf(requiredStage);
    return currentIdx >= requiredIdx;
  }

  /** Returns the tech stage required to unlock this building kind */
  getUnlockStage(buildingKind: BuildingKind): TechStage {
    return BUILDING_UNLOCK_STAGE[buildingKind] ?? TechStage.NascentSoul;
  }

  drainDamageQueue(): DamageEvent[] {
    const events = [...this.damageQueue];
    this.damageQueue = [];
    return events;
  }

  applyMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'WorldState':
        this.applyWorldSnapshot(msg);
        break;
      case 'DeltaState':
        this.applyDelta(msg.deltas);
        break;
      case 'PlayerJoined':
        if (!this.players.find(p => p.id === msg.player.id)) {
          this.players.push(msg.player);
        }
        // First player joined is us
        if (this.localPlayerId === null) {
          this.localPlayerId = msg.player.id;
          this.spiritQi = msg.player.spirit_qi;
          this.spiritStone = msg.player.spirit_stone;
          this.herb = msg.player.herb;
          this.pill = msg.player.pill;
          this.artifact = msg.player.artifact;
          this.puppetCores = msg.player.puppet_cores;
          this.techStage = msg.player.tech_stage ?? TechStage.Foundation;
        }
        break;
      case 'PlayerLeft':
        this.players = this.players.filter(p => p.id !== msg.player_id);
        break;
      case 'ResearchProgress':
        this.researchState = {
          active: true,
          progress: msg.progress,
          duration: msg.duration,
          targetStage: msg.target_stage,
        };
        break;
      case 'ResearchComplete':
        this.researchState = null;
        // Map string to TechStage enum
        const stageMap: Record<string, TechStage> = {
          'Foundation': TechStage.Foundation,
          'GoldenCore': TechStage.GoldenCore,
          'NascentSoul': TechStage.NascentSoul,
        };
        this.techStage = stageMap[msg.new_stage] ?? TechStage.Foundation;
        break;
      case 'DamageDealt':
        this.damageQueue.push({
          targetId: msg.target_id,
          amount: msg.amount,
          attackerId: msg.attacker_id,
        });
        break;
      case 'Error':
        console.error('Server error:', msg.message);
        break;
      case 'Pong':
        break;
    }
  }
}
